import * as L from "leaflet";
import 'leaflet.markercluster/dist/leaflet.markercluster';
import { MapPoint } from "../interfaces/map-point";

export class Map {
  private allMarkers: L.Marker[] = [];
  private map: L.Map;
  private markerGroup: L.MarkerClusterGroup;

  constructor(points: MapPoint[]) {

    const maxZoom = 19;
    const streetLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: maxZoom
    });

    const satelliteLayer = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: '© <a href="https://www.esri.com/">Esri</a>',
      }
    );

    this.map = L.map("map", {
      center: [0, 0],
      zoom: 13,
      maxZoom: maxZoom,
      layers: [streetLayer],
    });

    const baseMaps = {
      "Satellite": satelliteLayer,
      "Street Map": streetLayer,
    };

    L.control.layers(baseMaps).addTo(this.map);

    this.markerGroup = L.markerClusterGroup({
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div style="
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #0d9488;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: bold;
            border: 2px solid white;
          ">${count}</div>`,
          className: "custom-cluster-icon",
          iconSize: [40, 40],
        });
      }
    });
    this.map.addLayer(this.markerGroup);
    this.createMarkers(points);
  }

  updateMarkers(points: MapPoint[]) {
    this.allMarkers = [];
    this.markerGroup.clearLayers();
    this.createMarkers(points);
  }

  private createMarkers(points: MapPoint[]) {
    points.forEach((point) => {
      const popupContent = `<a href="${point.googleStreetViewLink}" target="_blank">Google Street View</a>`;

      const marker = L.marker([point.lat, point.lng], {
        icon: this.markerIconForColor("#0d9488"),
      }).bindPopup(popupContent);

      this.markerGroup.addLayer(marker);
      this.allMarkers.push(marker);

      marker.on("click", () => {
        this.allMarkers.forEach((m) => {
          m.setIcon(this.markerIconForColor(m === marker ? "#be123c" : "#0d9488"));
        });

        if (Office.context?.ui) {
          Office.context.ui.messageParent(
            JSON.stringify({
              action: "selectRangeAddress",
              rangeAddress: point.rangeAddress,
            })
          );
        } else {
          console.warn("Office.context.ui is not available.");
        }
      });
    });

    const latLngs = points.map((p) => [p.lat, p.lng]) as [number, number][];
    if (latLngs.length) {
      const bounds = L.latLngBounds(latLngs);
      this.map.fitBounds(bounds, { padding: [30, 30] });
    }
  }

  private markerIconForColor(color: string): L.DivIcon {
    return L.divIcon({
      html: `<div style="
        width: 16px;
        height: 16px;
        background-color: ${color};
        border: 2px solid white;
        border-radius: 50%;
        opacity: 0.9;
        box-sizing: border-box;"></div>`,
      className: "",
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
  }

  static convertToMapPoints(mapObjs: any[]): MapPoint[] {
    return mapObjs.map((o) => ({
      lat: o.Lat,
      lng: o.Lng,
      googleStreetViewLink: o.GoogleStreetViewLink,
      rangeAddress: o.RangeAddress,
    }));
  }
}
